import logger from '../../../services/logging/logging-service';
import { doMutation, doQuery, doSubscription, isGenericError, isGraphQlError } from '../../../services/graphql';
import { authStore } from '../../../global-store/subjects/auth';
import { parseErrorsGraphQL, parseDataParsely, parseDataParselyInPage } from '../../utils/parsers';
import { getFailureAction } from '../../utils/get-failure-action';
import { allowBodyScroll } from '../../../utils/body-scroll';
import { unsetZIndexAuto } from '../../../utils/z-index';
import { landToElement } from '../../../utils/land-to-element';

import { buildMutationPrivacyVariables, buildMutationVariables } from './helpers/build-variables';
import { setPreferencesCookie } from './helpers/cookies';
import { trackUserPreferences } from './helpers/analytics';
import { getHandlePreferencesListOpen } from './getters/get-handlers';

export const doWidgetQuery = ({ actions, commonVariables, widget, onSuccessCallback }) => {
  const auth = authStore.getValue();
  const graphQLDataParser = (dataToParse) =>
    parseDataParsely({ data: dataToParse, widgetId: commonVariables.widgetId });
  const variables = { ...commonVariables, token: auth.userToken };

  doQuery({
    query: widget.services.query,
    variables,
    onStart: actions?.setQueryStart,
    onFailure: (error, type) => {
      if (isGraphQlError(type)) {
        const errorObj = parseErrorsGraphQL(error);
        const failureAction = getFailureAction(actions, auth);
        const queryFailureAction = failureAction(errorObj).query;
        queryFailureAction(errorObj);
        logger.warn(`[${widget.name}] [doQuery() AppSync errors]`, error);
      }
      if (isGenericError(type)) {
        actions?.setQueryFailureGeneric(error);
        logger.warn(`[${widget.name}] [doQuery() generic error]`, error);
      }
    },
    onSuccess: (data) => {
      const parsedData = graphQLDataParser(data);
      actions?.setQuerySuccess(parsedData);
      setPreferencesCookie(parsedData.userPreferences, parsedData.widgetPreferences, parsedData.consent);
      actions?.setCurrentConsent(parsedData.consent);
      onSuccessCallback?.();
    },
  });
};

export const doWidgetQueryInPage = ({ actions, commonVariables, widget, onSuccessCallback }) => {
  const auth = authStore.getValue();
  const graphQLDataParser = (dataToParse) =>
    parseDataParselyInPage({ data: dataToParse, widgetId: commonVariables.widgetId });
  const variables = { ...commonVariables, token: auth.userToken };

  doQuery({
    query: widget.services.queryInPage,
    variables,
    onStart: actions?.setQueryStart,
    onFailure: (error, type) => {
      if (isGraphQlError(type)) {
        const errorObj = parseErrorsGraphQL(error);
        const failureAction = getFailureAction(actions, auth);
        const queryFailureAction = failureAction(errorObj).query;
        queryFailureAction(errorObj);
        logger.warn(`[${widget.name}] [doQuery() AppSync errors]`, error);
      }
      if (isGenericError(type)) {
        actions?.setQueryFailureGeneric(error);
        logger.warn(`[${widget.name}] [doQuery() generic error]`, error);
      }
    },
    onSuccess: (data) => {
      const parsedData = graphQLDataParser(data);
      actions?.setQuerySuccess(parsedData);
      setPreferencesCookie(parsedData.userPreferences, parsedData.widgetPreferences, parsedData.consent);
      actions?.setCurrentConsent(parsedData.consent);
      onSuccessCallback?.(parsedData);
    },
  });
};

export const doWidgetMutation = ({ actions, commonVariables, options, widget, onSuccessCallback }) => {
  const auth = authStore.getValue();
  const graphQLDataParser = (dataToParse) =>
    parseDataParsely({ data: dataToParse, widgetId: commonVariables.widgetId });

  const mutationVariables = buildMutationVariables({ ...commonVariables, token: auth.userToken }, options);

  doMutation({
    mutation: widget.services.mutation,
    variables: mutationVariables,
    onStart: actions?.setMutationStart,
    onFailure: (error, type) => {
      if (isGraphQlError(type)) {
        const errorObj = parseErrorsGraphQL(error);
        const failureAction = getFailureAction(actions, auth);
        const mutationFailureAction = failureAction(errorObj).mutation;
        mutationFailureAction(errorObj);
        logger.warn(`[${widget.name}] [doMutation() AppSync errors]`, error);
      }
      if (isGenericError(type)) {
        actions?.setMutationFailureGeneric(error);
        logger.warn(`[${widget.name}] [doMutation() generic error]`, error);
      }
    },
    onSuccess: (data) => {
      const parsedData = graphQLDataParser(data);
      actions?.setMutationSuccess(parsedData);
      trackUserPreferences(parsedData.userPreferences);
      setPreferencesCookie(parsedData.userPreferences, parsedData.widgetPreferences, parsedData.consent);
      onSuccessCallback?.();
    },
  });
};

export const doWidgetMutationInPage = ({ actions, commonVariables, options, widget, onSuccessCallback }) => {
  const auth = authStore.getValue();
  const graphQLDataParser = (dataToParse) =>
    parseDataParselyInPage({ data: dataToParse, widgetId: commonVariables.widgetId });

  const mutationVariables = buildMutationVariables({ ...commonVariables, token: auth.userToken }, options);

  doMutation({
    mutation: widget.services.mutationInPage,
    variables: mutationVariables,
    onStart: actions?.setMutationStart,
    onFailure: (error, type) => {
      if (isGraphQlError(type)) {
        const errorObj = parseErrorsGraphQL(error);
        const failureAction = getFailureAction(actions, auth);
        const mutationFailureAction = failureAction(errorObj).mutation;
        mutationFailureAction(errorObj);
        logger.warn(`[${widget.name}] [doMutation() AppSync errors]`, error);
      }
      if (isGenericError(type)) {
        actions?.setMutationFailureGeneric(error);
        logger.warn(`[${widget.name}] [doMutation() generic error]`, error);
      }
    },
    onSuccess: (data) => {
      const parsedData = graphQLDataParser(data);
      actions?.setMutationSuccess(parsedData);
      trackUserPreferences(parsedData.userPreferences);
      setPreferencesCookie(parsedData.userPreferences, parsedData.widgetPreferences, parsedData.consent);
      onSuccessCallback?.();
    },
  });
};

export const doWidgetMutationCleanMessage = (widget, code) => {
  const auth = authStore.getValue();

  doMutation({
    mutation: widget.services.mutationCleanMessage,
    variables: { code, token: auth.userToken },
    onFailure: (error, type) => {
      if (isGraphQlError(type)) {
        logger.warn(`[${widget.name}] [doMutation() AppSync errors]`, error);
      }
      if (isGenericError(type)) {
        logger.warn(`[${widget.name}] [doMutation() generic error]`, error);
      }
    },
  });
};

export const doWidgetMutationPrivacy = ({
  actions,
  commonVariables,
  consentOptionSelected,
  state,
  widget,
  tabId,
  onSuccessCallback,
}) => {
  const auth = authStore.getValue();
  const mutationPrivacyVariables = buildMutationPrivacyVariables(
    { ...commonVariables, token: auth.userToken },
    consentOptionSelected
  );
  doMutation({
    mutation: widget.services.mutationPrivacy,
    variables: mutationPrivacyVariables,
    onStart: actions?.setMutationPrivacyStart,
    onFailure: (error, type) => {
      if (isGraphQlError(type)) {
        const errorObj = parseErrorsGraphQL(error);
        const failureAction = getFailureAction(actions, auth);
        const mutationPrivacyFailureAction = failureAction(errorObj).mutationPrivacy;
        mutationPrivacyFailureAction(errorObj);
        logger.warn(`[${widget.name}] [doMutation() AppSync errors]`, error);
      }
      if (isGenericError(type)) {
        actions?.setMutationPrivacyFailureGeneric(error);
        logger.warn(`[${widget.name}] [doMutation() generic error]`, error);
      }
    },
    onSuccess: () => {
      actions?.setMutationPrivacySuccess();
      allowBodyScroll('DESKTOP');
      unsetZIndexAuto('o-background-offset-right-slim', 'MOBILE_PORTRAIT');
      actions?.setCurrentConsent(consentOptionSelected);
      if (consentOptionSelected === false) {
        landToElement.set({ elementId: widget.htmlId, tabId });
        document.location = window.location;
      }
      if (consentOptionSelected === true) {
        const handlePreferencesListOpen = getHandlePreferencesListOpen(widget, actions);
        actions.closeModalPrivacy();
        handlePreferencesListOpen(consentOptionSelected);
      }
      if (widget.messageIdToClean && state.messages?.some((message) => message.id === widget.messageIdToClean)) {
        doWidgetMutationCleanMessage(widget, widget.messageIdToClean);
      }
      onSuccessCallback?.();
    },
  });
};

export const doWidgetSubscription = ({ actions, commonVariables, state, widget, onSuccessCallback }) => {
  const auth = authStore.getValue();
  const graphQLDataParser = (dataToParse) =>
    parseDataParsely({ data: dataToParse, widgetId: commonVariables.widgetId });

  state.unsubscribe?.();

  const subscription = doSubscription({
    subscription: widget.services.subscription,
    variables: { ...commonVariables, token: auth.userToken },
    onNext: (dataSubscription) => {
      const parsedData = graphQLDataParser(dataSubscription.data);
      actions?.setSubscriptionNewDataSuccess(parsedData);
      actions?.setCurrentConsent(parsedData.consent);
      onSuccessCallback?.();
    },
    onFailure: (error) => {
      actions?.setSubscriptionFailure(error);
      logger.warn(`[${widget.name}] [doSubscription()] error`, error);
    },
  });

  actions.setSubscriptionStart(subscription.unsubscribe);
};

export const doWidgetSubscriptionInPage = ({ actions, commonVariables, state, widget, onSuccessCallback }) => {
  const auth = authStore.getValue();
  const graphQLDataParser = (dataToParse) =>
    parseDataParselyInPage({ data: dataToParse, widgetId: commonVariables.widgetId });

  state.unsubscribe?.();

  const subscription = doSubscription({
    subscription: widget.services.subscriptionInPage,
    variables: { ...commonVariables, token: auth.userToken },
    onNext: (dataSubscription) => {
      const parsedData = graphQLDataParser(dataSubscription.data);
      actions?.setSubscriptionNewDataSuccess(parsedData);
      actions?.setCurrentConsent(parsedData.consent);
      onSuccessCallback?.();
    },
    onFailure: (error) => {
      actions?.setSubscriptionFailure(error);
      logger.warn(`[${widget.name}] [doSubscription()] error`, error);
    },
  });

  actions.setSubscriptionStart(subscription.unsubscribe);
};
